"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button/Button";
import UserDropdown from "@/components/header/UserDropdown";

const NAV_LINKS = [
  { label: "How it works", href: "/#how-it-works" },
  { label: "Product", href: "/#product" },
  { label: "Pricing", href: "/pricing" },
  { label: "FAQ", href: "/#faq" },
] as const;

export function Navbar() {
  const { data: session } = useSession();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  function closeMobile() {
    setMobileOpen(false);
  }

  return (
    <>
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-[#EBEBE5]/50">
        <nav
          className="mx-auto flex h-16 md:h-20 max-w-7xl items-center justify-between px-5 md:px-8"
          aria-label="Main navigation"
        >
          {/* Logo */}
          <Link
            href="/"
            className="shrink-0 flex items-center gap-3 hover:opacity-90 transition-opacity"
            aria-label="Repayas home"
          >
            <Image
              src="/images/logo/logo_small.webp"
              alt="Repayas logo"
              width={130}
              height={32}
              priority
              className="object-contain"
            />
          </Link>

          {/* Centre nav links — desktop only */}
          <ul className="hidden md:flex items-center gap-10 flex-1 justify-center">
            {NAV_LINKS.map(({ label, href }) => (
              <li key={href}>
                <Link
                  href={href}
                  className="text-base md:text-sm font-normal text-text-secondary hover:text-text-primary transition-colors duration-150"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>

          {/* Right actions — mobile and desktop */}
          <div className="flex items-center gap-3 shrink-0">
            {/* Mobile: UserDropdown (when logged in) */}
            {session && (
              <div className="md:hidden">
                <UserDropdown />
              </div>
            )}

            {/* Desktop actions */}
            <div className="hidden md:flex items-center gap-6">
              {session ? (
                <UserDropdown />
              ) : (
                <>
                  <Link
                    href="/signin"
                    className="text-base lg:text-sm h-11 px-5.5 flex justify-center items-center border border-transparent font-medium text-text-secondary hover:text-text-secondary hover:border-gray-400 rounded-lg transition-colors duration-150"
                  >
                    Sign in
                  </Link>
                  <Button
                    variant="primary"
                    size="sm"
                    href="/signup"
                    id="nav-cta-start-trial"
                    className="h-11 px-5.5 text-base lg:text-sm font-medium rounded-lg"
                  >
                    Start free trial
                  </Button>
                </>
              )}
            </div>

            {/* Hamburger — mobile only */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="md:hidden flex flex-col justify-center items-center w-10 h-10 gap-1.5 rounded-xl hover:bg-black/5 transition-colors shrink-0"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              aria-controls="mobile-drawer"
            >
              <span className="block h-0.5 w-5.5 bg-gray-800 rounded" />
              <span className="block h-0.5 w-5.5 bg-gray-800 rounded" />
              <span className="block h-0.5 w-5.5 bg-gray-800 rounded" />
            </button>
          </div>
        </nav>
      </header>

      {/* ── Mobile drawer ─────────────────────────────────────────────────────*/}

      {/* Backdrop */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={closeMobile}
            className="fixed inset-0 z-60 bg-black/40 md:hidden"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Drawer panel — slides in from the right */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            id="mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            className="fixed top-0 right-0 z-70 h-full w-[80vw] max-w-sm bg-white shadow-2xl flex flex-col md:hidden"
          >
            {/* Drawer header */}
            <div className="flex items-center justify-end px-6 h-16 border-b border-gray-100 shrink-0">
              <button
                type="button"
                onClick={closeMobile}
                className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Close menu"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <path
                    d="M1 1L17 17M17 1L1 17"
                    stroke="#374151"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            {/* Nav links */}
            <ul className="flex flex-col px-6 py-6 gap-1 flex-1 overflow-y-auto">
              {NAV_LINKS.map(({ label, href }) => (
                <li key={href}>
                  <Link
                    href={href}
                    onClick={closeMobile}
                    className="block py-3 text-lg font-normal text-text-primary  transition-colors duration-150 border-b border-gray-100 last:border-0"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* Auth actions — pinned to bottom */}
            <div className="px-6 py-6 border-t border-gray-100 shrink-0">
              {!session && (
                <div className="flex flex-col gap-3">
                  <Button
                    variant="primary"
                    size="md"
                    href="/signup"
                    id="mobile-drawer-cta-start-trial"
                    fullWidth
                  >
                    Start free trial
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    href="/signin"
                    id="mobile-drawer-cta-sign-in"
                    fullWidth
                  >
                    Sign in
                  </Button>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
